import React, { useEffect, useRef, useState } from 'react';
import {
  DashboardTopLayout,
  DashboardCardsGrid,
  DashboardLeaderboardWrapper,
  DashboardActivitySection,
} from './sectionStyles';
import { DashboardLeaderboard } from './DashboardLeaderboard';
import { DashboardActivityTable } from './DashboardActivityTable';

/**
 * RFC 0003 — Layout section: 2-column top row (summary cards grid + leaderboard), then full-width activity table.
 * Does not fetch data; passes shared filter props to children.
 */
export type DashboardLeaderboardAndActivityFilters = {
  rangePreset: string;
  startDate: Date | null;
  endDate: Date | null;
  playersGroup?: string;
  playerNameSearch?: string;
};

type DashboardLeaderboardAndActivitySectionProps = DashboardLeaderboardAndActivityFilters & {
  /** Summary metrics cards (RFC 0002); stays in first grid column */
  summaryCards: React.ReactNode;
};

/**
 * Composes {@link DashboardTopLayout}, syncs leaderboard column height to summary cards grid on wide viewports,
 * and renders {@link DashboardLeaderboard} + {@link DashboardActivityTable} with the same filter props.
 */
export function DashboardLeaderboardAndActivitySection({
  summaryCards,
  rangePreset,
  startDate,
  endDate,
  playersGroup,
  playerNameSearch,
}: DashboardLeaderboardAndActivitySectionProps) {
  const cardsGridRef = useRef<HTMLDivElement | null>(null);
  const [leaderboardHeight, setLeaderboardHeight] = useState<number | null>(null);

  useEffect(() => {
    /** Aligns leaderboard card height to the summary cards grid on desktop; clears on narrow breakpoints. */
    const syncHeight = () => {
      // In stacked layout (mobile/tablet), let leaderboard use natural height.
      if (window.innerWidth <= 1100) {
        setLeaderboardHeight(null);
        return;
      }
      const next = cardsGridRef.current?.offsetHeight ?? null;
      setLeaderboardHeight(next && next > 0 ? next : null);
    };

    syncHeight();
    const canObserve = typeof ResizeObserver !== 'undefined';
    const observer = canObserve ? new ResizeObserver(syncHeight) : null;
    if (observer && cardsGridRef.current) observer.observe(cardsGridRef.current);
    window.addEventListener('resize', syncHeight);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', syncHeight);
    };
  }, [summaryCards]);

  const filters: DashboardLeaderboardAndActivityFilters = {
    rangePreset,
    startDate,
    endDate,
    playersGroup,
    playerNameSearch,
  };

  return (
    <>
      <DashboardTopLayout>
        <DashboardCardsGrid ref={cardsGridRef}>{summaryCards}</DashboardCardsGrid>
        <DashboardLeaderboardWrapper style={leaderboardHeight ? { height: `${leaderboardHeight}px` } : undefined}>
          <DashboardLeaderboard {...filters} />
        </DashboardLeaderboardWrapper>
      </DashboardTopLayout>

      <DashboardActivitySection>
        <DashboardActivityTable playersGroup={playersGroup} playerNameSearch={playerNameSearch} />
      </DashboardActivitySection>
    </>
  );
}
